import React, { useState } from "react";
import { Pencil, Plus, RefreshCw, Server, Trash2 } from "lucide-react";

import { Counted, ListCard, ListEmpty, ListRow, ListTile } from "@/shared/components/ListCard";
import { Chip, GhostButton, IconButton } from "@/shared/components/ui-kit";
import { favicon } from "@/shared/lib/sites";
import { McpServerDialog } from "./McpServerDialog";
import {
  useMcpServers, useRefreshMcpServer, useSetMcpLinkEnabled, useUnlinkMcpServer,
} from "./hooks/useMcpServers";

import type { McpServer } from "@/shared/types";

/* The agent's MCP servers (backend/modules/mcp/README.md): one row per workspace server
   mounted on this agent. Clicking a row switches its tools off or on for this
   agent only; the pencil edits the server itself (every agent it is on sees
   the change); the bin takes it off this agent and leaves it in the workspace. */

function McpRow({ agentId, server, onEdit }: { agentId: string; server: McpServer; onEdit: () => void }) {
  const toggle = useSetMcpLinkEnabled();
  const unlink = useUnlinkMcpServer();
  const refresh = useRefreshMcpServer();
  const on = server.link?.enabled !== false;
  const broken = server.status === "needs_auth";
  const logo = favicon(server.url);

  return (
    <ListRow
      onClick={() => toggle.mutate({ id: server.id, agentId, enabled: !on })}
      rowTitle={server.url}
      lead={
        <ListTile className={on ? "" : "opacity-40 grayscale"}>
          {logo
            ? <img src={logo} alt="" className="w-4 h-4 rounded-[3px]" />
            : <span className="text-[12px] font-mono font-semibold uppercase">{server.name[0]}</span>}
        </ListTile>
      }
      title={server.name}
      desc={<><span className="font-mono uppercase text-[11px] tracking-[0.04em]">{server.transport}</span> · {server.url}</>}
      muted={!on}
    >
      <IconButton
        onClick={(e) => { e.stopPropagation(); refresh.mutate(server.id); }}
        title="Refresh tools"
        className="hidden group-hover:flex"
      >
        <RefreshCw className={`w-3 h-3 ${refresh.isPending ? "animate-spin" : ""}`} strokeWidth={1.75} />
      </IconButton>
      <IconButton onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Configure server" className="hidden group-hover:flex">
        <Pencil className="w-3 h-3" strokeWidth={1.75} />
      </IconButton>
      <IconButton
        onClick={(e) => { e.stopPropagation(); unlink.mutate({ id: server.id, agentId }); }}
        title="Remove from this agent"
        className="hidden group-hover:flex hover:text-[var(--danger)]"
      >
        <Trash2 className="w-3 h-3" strokeWidth={1.75} />
      </IconButton>
      {broken ? (
        <span onClick={(e) => { e.stopPropagation(); onEdit(); }}><Chip tone="danger">Reconnect</Chip></span>
      ) : !on ? (
        <Chip>Off</Chip>
      ) : (
        <span className="shrink-0 flex items-center gap-1 text-[11px] font-medium tabular-nums text-[var(--success)]">
          <span className="w-[5px] h-[5px] rounded-full bg-[var(--success)]" />
          {server.tool_count} tools
        </span>
      )}
    </ListRow>
  );
}

export function McpServers({ agentId }: { agentId: string }) {
  const { data, isPending } = useMcpServers(agentId);
  const [dialog, setDialog] = useState<{ server?: McpServer } | null>(null);
  const rows = data ?? [];

  return (
    <>
      <ListCard
        label={isPending ? "Loading servers…" : <Counted n={rows.length} noun="server" />}
        action={<GhostButton onClick={() => setDialog({})} className="h-7 px-2.5"><Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add MCP server</GhostButton>}
      >
        {!isPending && rows.length === 0 && (
          <ListEmpty icon={Server} title="No MCP servers yet" hint="Add a server by URL to give the agent tools that aren't in the app catalog — internal APIs, databases, your own services." />
        )}
        {rows.map((s) => (
          <McpRow key={s.id} agentId={agentId} server={s} onEdit={() => setDialog({ server: s })} />
        ))}
      </ListCard>
      {dialog && (
        <McpServerDialog
          key={dialog.server?.id ?? "new"}
          agentId={agentId}
          server={dialog.server}
          linked={rows.map((s) => s.id)}
          onOpenChange={(open) => !open && setDialog(null)}
        />
      )}
    </>
  );
}
